import React, { useState, useEffect } from 'react';
import { useCompanyNotifications } from '../../../utils/notificationUtils';
import { PageHeader, PageTitle } from '../../../styles/components';
import { applicationStatus } from '../../../data/mock/studentInternships';
import styled from 'styled-components';
import { 
  Card, 
  Button, 
  Input, 
  Select, 
  glassDeep, 
  glassHover,
  floatAnimation
} from '../../../styles/components/common';

// Styled components for the enhanced UI
const Container = styled.div`
  padding: 1.5rem;
`;

const FilterSection = styled(Card)`
  ${glassDeep}
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    ${glassHover}
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ApplicationCard = styled(Card)`
  ${glassDeep}
  margin-bottom: 1rem;
  padding: 1.5rem;
  border-radius: 16px;
  transition: all 0.3s ease;
  color: white;
  
  &:hover {
    ${glassHover}
    transform: translateY(-2px);
  }
`;

const ApplicationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  color: white;
`;

const ApplicationInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  color: white;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  color: white;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  background-color: ${props => props.color};
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  ${glassDeep}
  border-radius: 16px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow: auto;
  animation: ${floatAnimation} 0.3s ease-out;
  color: white;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  color: white;
`;

const CloseButton = styled.button`
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 1.5rem;
  padding: 0.25rem 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    transform: scale(1.1);
  }
`;

const DetailSection = styled(Card)`
  ${glassDeep}
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  border-radius: 16px;
  color: white;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  color: white;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

// New styled components for document viewer
const DocumentViewer = styled(ModalContent)`
  max-width: 900px;
`;

const DocumentHeader = styled(ModalHeader)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding-bottom: 1rem;
`;

const DocumentContent = styled.div`
  padding: 1.5rem;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 1rem;
  max-height: 60vh;
  overflow-y: auto;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const DocumentSection = styled.div`
  margin-bottom: 1.5rem;
  
  h4 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding-bottom: 0.25rem;
  }
`;

const DocumentList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  
  li {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    
    &:before {
      content: "•";
      position: absolute;
      left: 0;
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DocumentItem = styled.div`
  margin-bottom: 0.5rem;
`;

const ApplicationsManagement = () => {
  // Transform the applicationStatus data to match the expected structure
  const transformedApplications = applicationStatus.map(app => ({
    id: app.id,
    jobTitle: app.position,
    studentName: `Student ${app.id.split('-')[2]}`, // Generate a name based on ID
    studentEmail: `student${app.id.split('-')[2]}@example.com`,
    studentPhone: `555-${app.id.split('-')[2]}-${app.id.split('-')[2]}`,
    studentMajor: 'Computer Science', // Default value
    studentGPA: 3.5, // Default value
    appliedDate: app.appliedDate,
    status: app.status.toLowerCase(),
    resumeUrl: '#',
    coverLetterUrl: '#',
    interviewDate: app.interviewDate || null,
    companyLogo: app.companyLogo
  }));

  const [applications, setApplications] = useState(transformedApplications);
  const [filteredApplications, setFilteredApplications] = useState(transformedApplications);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [documentViewer, setDocumentViewer] = useState({ isOpen: false, type: null, data: null });
  const { notifyApplicationStatusChange } = useCompanyNotifications();

  // Define unique job titles for filter
  const jobTitles = [...new Set(applications.map(app => app.jobTitle))];

  // Update filtered applications when filters change
  useEffect(() => {
    let filtered = applications;
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.studentEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.studentMajor.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    // Apply job filter
    if (jobFilter) {
      filtered = filtered.filter(app => app.jobTitle === jobFilter);
    }
    
    setFilteredApplications(filtered);
  }, [applications, searchTerm, statusFilter, jobFilter]);

  // Handle status changes
  const handleStatusChange = (appId, newStatus) => {
    setApplications(prev => 
      prev.map(app => {
        if (app.id === appId) {
          // If status is changed to "current intern", add start date
          if (newStatus === "current intern" && app.status !== "current intern") {
            return { 
              ...app, 
              status: newStatus,
              startDate: new Date().toISOString().split('T')[0],
              endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0] // Default 3 months
            };
          }
          // If status is changed to "internship complete", ensure there's an end date
          else if (newStatus === "internship complete") {
            return { 
              ...app, 
              status: newStatus,
              endDate: app.endDate || new Date().toISOString().split('T')[0]
            };
          }
          return { ...app, status: newStatus };
        }
        return app;
      })
    );
    
    const application = applications.find(app => app.id === appId);
    
    // Notify about application status change if accepted or rejected
    if (newStatus === 'accepted' || newStatus === 'rejected') {
      notifyApplicationStatusChange(application, newStatus);
    }
  };

  // View application details
  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
  };

  // View document (resume or cover letter)
  const viewDocument = (type, application) => {
    // Generate mock data based on the application
    const mockData = {
      resume: {
        personalInfo: {
          name: application.studentName,
          email: application.studentEmail,
          phone: application.studentPhone,
          location: "San Francisco, CA",
          linkedin: "linkedin.com/in/studentprofile"
        },
        summary: "Recent Computer Science graduate with a strong foundation in software development and a passion for creating innovative solutions. Seeking opportunities to apply my technical skills and contribute to meaningful projects.",
        education: [
          {
            degree: "Bachelor of Science in Computer Science",
            school: "University of Technology",
            location: "San Francisco, CA",
            date: "2019 - 2023",
            gpa: application.studentGPA,
            relevantCourses: ["Data Structures", "Algorithms", "Web Development", "Database Systems", "Software Engineering"]
          }
        ],
        experience: [
          {
            title: "Software Development Intern",
            company: "Tech Innovations Inc.",
            location: "San Francisco, CA",
            date: "June 2022 - August 2022",
            responsibilities: [
              "Assisted in developing and maintaining web applications using React and Node.js",
              "Collaborated with the development team to implement new features and fix bugs",
              "Participated in code reviews and contributed to improving code quality",
              "Created documentation for new features and API endpoints"
            ]
          },
          {
            title: "IT Support Assistant",
            company: "University IT Department",
            location: "San Francisco, CA",
            date: "September 2021 - May 2023",
            responsibilities: [
              "Provided technical support to students and faculty",
              "Assisted with hardware and software troubleshooting",
              "Helped maintain computer labs and equipment",
              "Created user guides and documentation for common issues"
            ]
          }
        ],
        skills: [
          "Programming Languages: JavaScript, Python, Java, C++",
          "Web Technologies: HTML, CSS, React, Node.js, Express",
          "Databases: MongoDB, MySQL, PostgreSQL",
          "Tools & Platforms: Git, Docker, AWS, VS Code",
          "Soft Skills: Problem-solving, Teamwork, Communication, Time Management"
        ],
        projects: [
          {
            name: "E-commerce Platform",
            description: "Developed a full-stack e-commerce application with user authentication, product catalog, shopping cart, and payment integration.",
            technologies: "React, Node.js, Express, MongoDB, Stripe API"
          },
          {
            name: "Task Management App",
            description: "Created a collaborative task management application with real-time updates, user roles, and deadline tracking.",
            technologies: "React, Firebase, Material UI"
          }
        ]
      },
      coverLetter: {
        date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        recipient: "Hiring Manager",
        company: "Tech Innovations Inc.",
        address: "123 Tech Street, San Francisco, CA 94105",
        greeting: "Dear Hiring Manager,",
        introduction: `I am writing to express my strong interest in the ${application.jobTitle} position at ${application.companyLogo || "your company"}. As a recent Computer Science graduate with a GPA of ${application.studentGPA}, I am excited about the opportunity to contribute my skills and enthusiasm to your team.`,
        body: [
          `My academic background in Computer Science has provided me with a solid foundation in software development principles and practices. Through my coursework and projects, I have gained experience in developing web applications, working with databases, and implementing algorithms. I am particularly drawn to ${application.companyLogo || "your company"} because of its innovative approach to technology and commitment to creating impactful solutions.`,
          `During my internship at Tech Innovations Inc., I had the opportunity to work on real-world projects that enhanced my technical skills and taught me the importance of collaboration and effective communication. I developed and maintained web applications using React and Node.js, participated in code reviews, and contributed to improving code quality. These experiences have prepared me to make meaningful contributions to your team.`,
          `I am particularly excited about the opportunity to work with your team on [specific project or technology mentioned in the job description]. My experience with [relevant technology or skill] aligns well with the requirements of this position, and I am confident that I can bring valuable insights and fresh perspectives to your projects.`
        ],
        conclusion: `I am enthusiastic about the possibility of joining ${application.companyLogo || "your company"} and contributing to its continued success. I welcome the opportunity to discuss how my skills and experiences align with your needs. Thank you for considering my application.`,
        closing: "Sincerely,",
        signature: application.studentName
      }
    };

    setDocumentViewer({
      isOpen: true,
      type,
      data: mockData[type]
    });
  };

  // Get status display text and color based on status
  const getStatusDisplay = (status) => {
    switch(status) {
      case 'pending':
        return { text: 'Pending', color: 'var(--warning-color)' };
      case 'finalized':
        return { text: 'Finalized', color: 'var(--info-color)' };
      case 'interview':
        return { text: 'Finalized', color: 'var(--success-light-color)' };
      case 'accepted':
        return { text: 'Accepted', color: 'var(--success-color)' };
      case 'rejected':
        return { text: 'Rejected', color: 'var(--danger-color)' };
      case 'current intern':
        return { text: 'Current Intern', color: '#2e7d32' };
      case 'internship complete':
        return { text: 'Internship Complete', color: '#9e9e9e' };
      default:
        return { text: status.charAt(0).toUpperCase() + status.slice(1), color: 'var(--primary-color)' };
    }
  };

  return (
    <Container>
      <PageHeader>
        <PageTitle>Applications Management</PageTitle>
      </PageHeader>
      
      {/* Filters */}
      <FilterSection>
        <FilterGrid>
          <div>
            <label htmlFor="search" style={{ color: 'white' }}>Search</label>
            <Input 
              type="text" 
              id="search" 
              placeholder="Search by name, email, or major" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div>
            <label htmlFor="status" style={{ color: 'white' }}>Filter by Status</label>
            <Select 
              id="status" 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="finalized">Finalized</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="current intern">Current Intern</option>
              <option value="internship complete">Internship Complete</option>
            </Select>
          </div>
          
          <div>
            <label htmlFor="job" style={{ color: 'white' }}>Filter by Job</label>
            <Select 
              id="job" 
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
            >
              <option value="">All Jobs</option>
              {jobTitles.map((title, index) => (
                <option key={index} value={title}>{title}</option>
              ))}
            </Select>
          </div>
        </FilterGrid>
        
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Button 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setJobFilter('');
            }}
            variant="outline"
          >
            Clear Filters
          </Button>
        </div>
      </FilterSection>
      
      {/* Applications List */}
      <div style={{ marginBottom: '2rem' }}>
        {filteredApplications.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '2rem', color: 'white' }}>
            <p>No applications match your search criteria.</p>
          </Card>
        ) : (
          filteredApplications.map(application => (
            <ApplicationCard key={application.id}>
              <ApplicationHeader>
                <ApplicationInfo>
                  <h3 style={{ marginBottom: '0.5rem', color: 'white' }}>{application.studentName}</h3>
                  <InfoRow>
                    <span><strong>Applied for:</strong> {application.jobTitle}</span>
                    <span><strong>Date:</strong> {application.appliedDate}</span>
                    <span><strong>Major:</strong> {application.studentMajor}</span>
                    <span><strong>GPA:</strong> {application.studentGPA}</span>
                  </InfoRow>
                  {application.interviewDate && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span><strong>Interview Date:</strong> {application.interviewDate}</span>
                    </div>
                  )}
                  {(application.status === 'current intern' || application.status === 'internship complete') && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span><strong>Start Date:</strong> {application.startDate}</span>
                      {application.endDate && <span style={{ marginLeft: '1rem' }}><strong>End Date:</strong> {application.endDate}</span>}
                    </div>
                  )}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span><strong>Email:</strong> {application.studentEmail}</span><br />
                    <span><strong>Phone:</strong> {application.studentPhone}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button 
                      variant="secondary" 
                      size="small" 
                      onClick={() => viewDocument('resume', application)}
                    >
                      View Resume
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="small" 
                      onClick={() => viewDocument('coverLetter', application)}
                    >
                      View Cover Letter
                    </Button>
                  </div>
                </ApplicationInfo>
                <ActionButtons>
                  <StatusBadge color={getStatusDisplay(application.status).color}>
                    {getStatusDisplay(application.status).text}
                  </StatusBadge>
                  <Select
                    value={application.status}
                    onChange={(e) => handleStatusChange(application.id, e.target.value)}
                    style={{ marginBottom: '0.5rem', minWidth: '150px' }}
                  >
                    <option value="pending">Pending</option>
                    <option value="finalized">Finalized</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="current intern">Current Intern</option>
                    <option value="internship complete">Internship Complete</option>
                  </Select>
                  <Button 
                    onClick={() => viewApplicationDetails(application)}
                    size="small"
                  >
                    View Details
                  </Button>
                </ActionButtons>
              </ApplicationHeader>
            </ApplicationCard>
          ))
        )}
      </div>
      
      {/* Application Details Modal */}
      {selectedApplication && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <h2>Application Details</h2>
              <CloseButton onClick={() => setSelectedApplication(null)}>
                &times;
              </CloseButton>
            </ModalHeader>
            
            <DetailSection>
              <h3>Student Information</h3>
              <DetailGrid>
                <div>
                  <p><strong>Name:</strong> {selectedApplication.studentName}</p>
                  <p><strong>Email:</strong> {selectedApplication.studentEmail}</p>
                  <p><strong>Phone:</strong> {selectedApplication.studentPhone}</p>
                </div>
                <div>
                  <p><strong>Major:</strong> {selectedApplication.studentMajor}</p>
                  <p><strong>GPA:</strong> {selectedApplication.studentGPA}</p>
                  <p><strong>Applied on:</strong> {selectedApplication.appliedDate}</p>
                </div>
              </DetailGrid>
              
              {selectedApplication.interviewDate && (
                <div style={{ marginTop: '1rem' }}>
                  <p><strong>Interview Date:</strong> {selectedApplication.interviewDate}</p>
                </div>
              )}
              
              {(selectedApplication.status === 'current intern' || selectedApplication.status === 'internship complete') && (
                <div style={{ marginTop: '1rem' }}>
                  <p><strong>Start Date:</strong> {selectedApplication.startDate || 'Not set'}</p>
                  <p><strong>End Date:</strong> {selectedApplication.endDate || 'Not set'}</p>
                </div>
              )}
            </DetailSection>
            
            <DetailSection>
              <h3>Application Status</h3>
              <div style={{ marginBottom: '1rem' }}>
                <label htmlFor="applicationStatus" style={{ color: 'white' }}>Current Status:</label>
                <Select
                  id="applicationStatus"
                  value={selectedApplication.status}
                  onChange={(e) => {
                    handleStatusChange(selectedApplication.id, e.target.value);
                    // Update the selected application status in the local state
                    setSelectedApplication({
                      ...selectedApplication,
                      status: e.target.value,
                      // If changing to current intern, add start date
                      ...(e.target.value === 'current intern' && selectedApplication.status !== 'current intern' 
                        ? {
                            startDate: new Date().toISOString().split('T')[0],
                            endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0]
                          } 
                        : {}),
                      // If changing to internship complete, ensure end date
                      ...(e.target.value === 'internship complete' 
                        ? {
                            endDate: selectedApplication.endDate || new Date().toISOString().split('T')[0]
                          } 
                        : {})
                    });
                  }}
                  style={{ marginBottom: '0.5rem', width: '100%' }}
                >
                  <option value="pending">Pending</option>
                  <option value="finalized">Finalized</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="current intern">Current Intern</option>
                  <option value="internship complete">Internship Complete</option>
                </Select>
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button onClick={() => viewDocument('resume', selectedApplication)}>View Resume</Button>
                <Button onClick={() => viewDocument('coverLetter', selectedApplication)}>View Cover Letter</Button>
              </div>
            </DetailSection>
            
            {selectedApplication.status !== 'internship complete' && (
              <DetailSection>
                <h3>Appointment Scheduling</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="interviewDate" style={{ color: 'white' }}>Interview Date:</label>
                  <Input type="date" id="interviewDate" style={{ width: '100%', color: 'white' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="interviewTime" style={{ color: 'white' }}>Interview Time:</label>
                  <Input type="time" id="interviewTime" style={{ width: '100%', color: 'white' }} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label htmlFor="interviewNotes" style={{ color: 'white' }}>Notes:</label>
                  <Input as="textarea" id="interviewNotes" rows="3" placeholder="Add notes about this interview" style={{ width: '100%', color: 'white' }} />
                </div>
                <Button>Schedule Interview</Button>
              </DetailSection>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Document Viewer Modal */}
      {documentViewer.isOpen && (
        <ModalOverlay>
          <DocumentViewer>
            <DocumentHeader>
              <h2>{documentViewer.type === 'resume' ? 'Resume' : 'Cover Letter'}</h2>
              <CloseButton onClick={() => setDocumentViewer({ isOpen: false, type: null, data: null })}>
                &times;
              </CloseButton>
            </DocumentHeader>
            
            <DocumentContent>
              {documentViewer.type === 'resume' && documentViewer.data && (
                <>
                  <DocumentSection>
                    <h3>{documentViewer.data.personalInfo.name}</h3>
                    <DocumentGrid>
                      <DocumentItem>
                        <strong>Email:</strong> {documentViewer.data.personalInfo.email}
                      </DocumentItem>
                      <DocumentItem>
                        <strong>Phone:</strong> {documentViewer.data.personalInfo.phone}
                      </DocumentItem>
                      <DocumentItem>
                        <strong>Location:</strong> {documentViewer.data.personalInfo.location}
                      </DocumentItem>
                      <DocumentItem>
                        <strong>LinkedIn:</strong> {documentViewer.data.personalInfo.linkedin}
                      </DocumentItem>
                    </DocumentGrid>
                  </DocumentSection>
                  
                  <DocumentSection>
                    <h4>Professional Summary</h4>
                    <p>{documentViewer.data.summary}</p>
                  </DocumentSection>
                  
                  <DocumentSection>
                    <h4>Education</h4>
                    {documentViewer.data.education.map((edu, index) => (
                      <div key={index} style={{ marginBottom: '1rem' }}>
                        <h5>{edu.degree}</h5>
                        <p>{edu.school}, {edu.location} | {edu.date}</p>
                        <p>GPA: {edu.gpa}</p>
                        <p><strong>Relevant Courses:</strong> {edu.relevantCourses.join(', ')}</p>
                      </div>
                    ))}
                  </DocumentSection>
                  
                  <DocumentSection>
                    <h4>Experience</h4>
                    {documentViewer.data.experience.map((exp, index) => (
                      <div key={index} style={{ marginBottom: '1rem' }}>
                        <h5>{exp.title}</h5>
                        <p>{exp.company}, {exp.location} | {exp.date}</p>
                        <DocumentList>
                          {exp.responsibilities.map((resp, i) => (
                            <li key={i}>{resp}</li>
                          ))}
                        </DocumentList>
                      </div>
                    ))}
                  </DocumentSection>
                  
                  <DocumentSection>
                    <h4>Skills</h4>
                    <DocumentList>
                      {documentViewer.data.skills.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </DocumentList>
                  </DocumentSection>
                  
                  <DocumentSection>
                    <h4>Projects</h4>
                    {documentViewer.data.projects.map((project, index) => (
                      <div key={index} style={{ marginBottom: '1rem' }}>
                        <h5>{project.name}</h5>
                        <p>{project.description}</p>
                        <p><strong>Technologies:</strong> {project.technologies}</p>
                      </div>
                    ))}
                  </DocumentSection>
                </>
              )}
              
              {documentViewer.type === 'coverLetter' && documentViewer.data && (
                <>
                  <div style={{ textAlign: 'right', marginBottom: '1rem' }}>
                    <p>{documentViewer.data.date}</p>
                    <p>{documentViewer.data.recipient}</p>
                    <p>{documentViewer.data.company}</p>
                    <p>{documentViewer.data.address}</p>
                  </div>
                  
                  <p>{documentViewer.data.greeting}</p>
                  
                  <p>{documentViewer.data.introduction}</p>
                  
                  {documentViewer.data.body.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                  
                  <p>{documentViewer.data.conclusion}</p>
                  
                  <p>{documentViewer.data.closing}</p>
                  <p style={{ marginTop: '2rem' }}>{documentViewer.data.signature}</p>
                </>
              )}
            </DocumentContent>
          </DocumentViewer>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ApplicationsManagement;